/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module bulb-image/bulbimagetoolbar
 */
import {
  Plugin, isWidget, WidgetToolbarRepository, type ViewElement,
} from 'ckeditor5';
import { isObject } from 'lodash';
import { getToolBarConfig } from '../../editorcustomconfig.constant';

/**
 * The image toolbar plugin. It creates a toolbar for images that shows up when the figure is selected.
 *
 * Instances of toolbar components (e.g. buttons) are created based on the
 * {@link module:bulb-image/BulbImageToolbar#toolbar `bulbImage.toolbar` configuration option}.
 *
 * @extends Plugin
 */
export default class BulbImageToolbar extends Plugin {

  /**
   * @inheritDoc
   */
  static get requires() {
    return [WidgetToolbarRepository] as const;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName(): string {
    return 'BulbImageToolbar' as const;
  }

  /**
   * @inheritDoc
   */
  public afterInit(): void {
    const { editor } = this;
    const widgetToolbarRepository = editor.plugins.get(WidgetToolbarRepository);

    widgetToolbarRepository.register('bulbImage', {
      items: this.normalizeDeclarativeConfig(getToolBarConfig('bulbImage', CKEBulbData.readOnly)),
      getRelatedElement: this.getSelectedMediaViewWidget,
    });
  }

  /**
   * Convert the dropdown definitions to their keys registered in the ComponentFactory.
   * The registration precess should be handled by the plugin which handles the UI of a particular feature.
   */
  private normalizeDeclarativeConfig(config) {
    return config.map((item: { name: any; }) => (isObject(item) ? item.name : item));
  }

  /**
   * Method that delegates whether to show the bulb image toolbar or not.
   *
   * @param selection
   * @returns {null|module:engine/model/element~ModelElement|module:engine/view/element~ModelElement}
   * @private
   */
  private getSelectedMediaViewWidget(selection): ViewElement {
    const selectionPosition = selection.getFirstPosition();
    if (!selectionPosition) {
      return null;
    }
    const viewElement = selection.getSelectedElement();

    if (viewElement && !!viewElement.getCustomProperty('image') && isWidget(viewElement)) {
      return viewElement;
    }
    let { parent } = selectionPosition;

    if (!viewElement && (!parent.is('element') || (parent.is('element') && parent.name === 'figcaption'))) {
      return null;
    }

    while (parent) {
      if (parent.is('element') && isWidget(parent)) {
        return parent;
      }
      parent = parent.parent;
    }

    return null;
  }
}
